// Captures console errors and network failures for bug reports

interface LogEntry {
  level: "error" | "warn" | "log";
  message: string;
  timestamp: string;
}

interface NetworkError {
  url: string;
  method: string;
  status: number;
  statusText: string;
  timestamp: string;
}

const MAX_LOGS = 50;
const consoleLogs: LogEntry[] = [];
const networkErrors: NetworkError[] = [];

// Intercept console.error and console.warn
const originalError = console.error;
const originalWarn = console.warn;

console.error = (...args: unknown[]) => {
  consoleLogs.push({
    level: "error",
    message: args.map((a) => (typeof a === "object" ? JSON.stringify(a) : String(a))).join(" "),
    timestamp: new Date().toISOString(),
  });
  if (consoleLogs.length > MAX_LOGS) consoleLogs.shift();
  originalError.apply(console, args);
};

console.warn = (...args: unknown[]) => {
  consoleLogs.push({
    level: "warn",
    message: args.map((a) => (typeof a === "object" ? JSON.stringify(a) : String(a))).join(" "),
    timestamp: new Date().toISOString(),
  });
  if (consoleLogs.length > MAX_LOGS) consoleLogs.shift();
  originalWarn.apply(console, args);
};

// Intercept failed fetch requests
const originalFetch = window.fetch;
window.fetch = async (...args: Parameters<typeof fetch>) => {
  const res = await originalFetch(...args);
  if (!res.ok) {
    const url = typeof args[0] === "string" ? args[0] : args[0] instanceof Request ? args[0].url : "unknown";
    const method = (args[1]?.method || "GET").toUpperCase();
    networkErrors.push({
      url,
      method,
      status: res.status,
      statusText: res.statusText,
      timestamp: new Date().toISOString(),
    });
    if (networkErrors.length > MAX_LOGS) networkErrors.shift();
  }
  return res;
};

// Capture unhandled errors
window.addEventListener("error", (event) => {
  consoleLogs.push({
    level: "error",
    message: `Unhandled: ${event.message} at ${event.filename}:${event.lineno}:${event.colno}`,
    timestamp: new Date().toISOString(),
  });
  if (consoleLogs.length > MAX_LOGS) consoleLogs.shift();
});

window.addEventListener("unhandledrejection", (event) => {
  consoleLogs.push({
    level: "error",
    message: `Unhandled Promise: ${event.reason?.message || event.reason || "unknown"}`,
    timestamp: new Date().toISOString(),
  });
  if (consoleLogs.length > MAX_LOGS) consoleLogs.shift();
});

export function getDiagnostics() {
  return {
    browser: {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      colorDepth: window.screen.colorDepth,
      online: navigator.onLine,
    },
    page: {
      url: window.location.href,
      path: window.location.pathname,
      timestamp: new Date().toISOString(),
    },
    consoleLogs: [...consoleLogs].slice(-20),
    networkErrors: [...networkErrors].slice(-10),
  };
}
